import React, { useState, useEffect, useRef } from 'react';
import { Mail, ArrowLeft, RefreshCw, Shield, CheckCircle } from 'lucide-react';

const OtpVerification = ({
  email,
  onVerificationSuccess,
  onBack,
  isLoading
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  const inputRefs = useRef([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all fields are filled
    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = Array(6).fill('').map((_, i) => pastedData[i] || '');
    setOtp(newOtp);
    
    if (pastedData.length === 6) {
      handleVerifyOtp(pastedData);
    }
  };

  const handleVerifyOtp = async (otpCode) => {
    setIsVerifying(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_Backend_Url}/api/user/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp: otpCode
        }),
      });

      const data = await response.json();

      if (response.ok) {
        onVerificationSuccess(data.token);
      } else {
        setError(data.message || 'Code OTP invalide');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setError('Erreur de vérification. Veuillez réessayer.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_Backend_Url}/api/user/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setTimeLeft(300);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        const data = await response.json();
        setError(data.message || 'Erreur lors de l\'envoi du code');
      }
    } catch (error) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-8 space-y-6 bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Vérification de votre email
        </h2>
        <p className="text-gray-600 text-sm">
          Nous avons envoyé un code de vérification à
        </p>
        <p className="text-[#008751] font-medium text-sm mt-1">
          {email}
        </p>
      </div>

      {/* OTP Input */}
      <div className="space-y-6">
        <div className="flex justify-center space-x-3">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008751] transition-colors ${
                error 
                  ? 'border-red-300 bg-red-50' 
                  : digit 
                    ? 'border-[#008751] bg-green-50' 
                    : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
              disabled={isVerifying || isLoading}
            />
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-600 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Timer and Resend */}
        <div className="text-center space-y-3">
          {!canResend ? (
            <p className="text-gray-500 text-sm">
              Code expire dans: <span className="font-mono font-bold text-[#008751]">{formatTime(timeLeft)}</span>
            </p>
          ) : (
            <p className="text-gray-500 text-sm">
              Vous n'avez pas reçu le code ?
            </p>
          )}

          <button
            onClick={handleResendOtp}
            disabled={!canResend || resendLoading}
            className={`inline-flex items-center space-x-2 text-sm font-medium transition-colors ${
              canResend && !resendLoading
                ? 'text-[#008751] hover:text-[#006d40] cursor-pointer'
                : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${resendLoading ? 'animate-spin' : ''}`} />
            <span>{resendLoading ? 'Envoi en cours...' : 'Renvoyer le code'}</span>
          </button>
        </div>

        {/* Loading State */}
        {(isVerifying || isLoading) && (
          <div className="flex items-center justify-center space-x-2 py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#008751]"></div>
            <span className="text-sm text-gray-600">
              {isVerifying ? 'Vérification...' : 'Création du compte...'}
            </span>
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={onBack}
          disabled={isVerifying || isLoading}
          className="w-full flex items-center justify-center space-x-2 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Modifier l'email</span>
        </button>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Instructions:</p>
            <ul className="space-y-1 text-xs">
              <li>• Vérifiez votre boîte de réception</li>
              <li>• Regardez aussi dans les spams</li>
              <li>• Le code est valide pendant 5 minutes</li>
              <li>• Saisissez les 6 chiffres reçus</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;